import React, { ButtonHTMLAttributes, ElementType, ReactNode } from 'react'
import { CSSObject } from 'styled-components'
import { IconName } from '~components/Icon'

export interface StylingButtonProps {
  /**
   * What color type should the button has?
   */
  color?: ColorType
  /**
   * What variant type should the button be?
   * Not all Color types has variants
   */
  variant?: VariantType
  /**
   * How large should the button be?
   */
  size?: SizeType
  /**
   * What shape should the button has?
   */
  shape?: ShapeType
  /**
   * Has disabled state?
   */
  disabled?: boolean
  /**
   * Has loading state?
   */
  isLoading?: boolean
  /**
   * Show success string or check icon?
   */
  isSuccess?: boolean | string
  /**
   * Change view to `success` when `isSuccess` is truthy?
   */
  coloredOnSuccess?: boolean
  /**
   * Has active state?
   */
  isActive?: boolean
  /**
   * Stretch the button to the full width of container?
   */
  isFullWidth?: boolean
  /**
   * Enable auto height, rather than from predefined values?
   */
  isHeightAuto?: boolean
}

interface CustomButtonProps {
  /**
   * Icon to insert before/after the button contents
   */
  icon?: IconName
  /**
   * Side of the icon
   */
  iconPosition?: IconPositionType
  /**
   * Custom loader element
   */
  customLoader?: JSX.Element

  /**
   * Custom element to render
   */
  as?: ElementType & ReactNode
  /**
   * Internal link to route
   */
  to?: string
  /**
   * External link
   */
  href?: string
  /**
   * Anchor element target
   */
  target?: string
  /**
   * Anchor element rel
   */
  rel?: 'noopener noreferrer' | 'nofollow'
  /**
   * Anchor element file name to download
   */
  download?: string

  /**
   * React ref
   */
  ref?: React.ForwardedRef<HTMLButtonElement>
}

export type ButtonProps = StylingButtonProps & CustomButtonProps & ButtonHTMLAttributes<HTMLButtonElement>

export interface StyledButtonProps extends StylingButtonProps {
  view: ViewType
  color: ColorType
  variant: VariantType
  size: SizeType
  shape: ShapeType
  isDisabled?: boolean
}

export const viewType = [
  'primary-solid',
  'primary-outlined',
  'primary-transparent',
  'base-outlined',
  'base-transparent',
  'danger-solid',
  'danger-outlined',
  'danger-transparent',
  'success-solid',
  'success-outlined',
  'success-transparent',
  'warning-solid',
  'warning-outlined',
  'warning-transparent',
  'info-solid',
  'info-outlined',
  'info-transparent',
] as const

export type ViewType = typeof viewType[number]

export type ColorType = 'primary' | 'base' | 'danger' | 'success' | 'warning' | 'info'

export type VariantType = 'solid' | 'outlined' | 'transparent'

export type SizeType = 'extra-small' | 'small' | 'medium' | 'large'

export type ShapeType = 'default' | 'square' | 'circle'

export type IconPositionType = 'left' | 'right'

export type Template = {
  static: CSSObject
  hover?: CSSObject
  active?: CSSObject
  focus?: CSSObject
  disabled?: CSSObject
}
